// IMPORTANT: Ensure you have run `npm install leaflet react-leaflet react-leaflet-markercluster leaflet.markercluster`
import React, { useEffect, useState, useMemo } from 'react';
import { getFirestore, collection, getDocs } from 'firebase/firestore';
import { decryptData } from '../../encryptionUtil'; // Adjust path as necessary
// react-leaflet provides React components for Leaflet maps
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
// react-leaflet-markercluster groups nearby markers into clusters
import MarkerClusterGroup from 'react-leaflet-markercluster';
// L is the Leaflet library itself
import L from 'leaflet';

// Import Leaflet's CSS for map styling
import 'leaflet/dist/leaflet.css';
// Import MarkerCluster CSS for styling marker clusters
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import { useTheme } from '../../components/ui/theme-provider'; // Import useTheme
import { cn } from "../../lib/utils"; // Import cn


// Fix for default marker icon issue with Webpack when using Leaflet
// This ensures that marker icons are loaded correctly.
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const UserMapPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const db = getFirestore();
  const { theme } = useTheme();

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      setError('');
      try {
        // 1. Fetch user data from Firestore
        const usersCollectionRef = collection(db, 'users');
        const querySnapshot = await getDocs(usersCollectionRef);
        const usersList = [];
        querySnapshot.forEach((doc) => {
          const userData = doc.data();
          // 2. Decrypt relevant fields (name, email, latitude, longitude)
          //    Latitude and longitude are assumed to be stored from a previous geocoding step (e.g., during profile save).
          const displayName = userData.name ? decryptData(userData.name) : (userData.displayName || 'N/A');
          const email = userData.email ? decryptData(userData.email) : (userData.email || 'N/A'); // Email might not always be encrypted
          const latitude = userData.latitude ? decryptData(userData.latitude) : null;
          const longitude = userData.longitude ? decryptData(userData.longitude) : null;

          // Only include users with valid, parsed latitude and longitude
          if (latitude && longitude) {
            usersList.push({
              id: doc.id,
              displayName,
              email,
              latitude: parseFloat(latitude),
              longitude: parseFloat(longitude),
            });
          }
        });
        setUsers(usersList);
      } catch (err) {
        console.error("Error fetching users:", err);
        setError('Failed to load user data. ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [db]);

  // Define geographical bounds for the UK to limit map panning
  const ukBounds = useMemo(() => [
    [49.5, -8.0], // Southwest corner
    [61.0, 2.0],  // Northeast corner
  ], []);

  if (loading) {
    return <div className={cn("p-4", theme === 'light' ? 'text-gray-800' : 'text-gray-200')}>Loading user map...</div>;
  }

  if (error) {
    return <div className={cn("p-4 text-red-500", theme === 'light' ? 'bg-red-100' : 'bg-red-900/30')}>{error}</div>;
  }

  return (
    <div className={cn("p-4 rounded-lg shadow", theme === 'light' ? 'bg-white' : 'bg-[#1E1E2D]')}>
      <h1 className={cn("text-2xl font-bold mb-4", theme === 'light' ? 'text-purple-700' : 'text-purple-300')}>User Location Map</h1>
      {users.length === 0 && !loading && (
        <p className={cn(theme === 'light' ? 'text-gray-600' : 'text-gray-400')}>No users with location data found.</p>
      )}
      {users.length > 0 && (
        <div style={{ height: '70vh', width: '100%' }} className="rounded-md overflow-hidden border border-gray-300 dark:border-gray-700">
          {/* 3. Map Display using react-leaflet */}
          <MapContainer
            center={[54.5, -2.5]} // Initial map center (UK)
            zoom={6}               // Initial zoom level
            scrollWheelZoom={true} // Allow zooming with mouse wheel
            style={{ height: '100%', width: '100%' }}
            maxBounds={ukBounds}   // Restrict map view to UK bounds
            minZoom={5}            // Prevent zooming out too far
          >
            {/*
              The TileLayer component loads map tiles from a tile server.
              Here, we are using OpenStreetMap (OSM) tiles, which are free to use.
              OSM provides the visual map background.
              This component does NOT perform geocoding; it only displays pre-rendered map images (tiles).
            */}
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {/*
              MarkerClusterGroup helps manage a large number of markers by grouping them
              into clusters at lower zoom levels.
            */}
            <MarkerClusterGroup>
              {/* 4. Data Overlay: Plotting user locations as Markers */}
              {users.map(user => (
                // For each user with coordinates, a Marker is placed on the map.
                <Marker key={user.id} position={[user.latitude, user.longitude]}>
                  {/* A Popup is shown when a marker is clicked, displaying user info. */}
                  <Popup>
                    <strong>{user.displayName}</strong><br />
                    {user.email}<br />
                    Lat: {user.latitude.toFixed(4)}, Lon: {user.longitude.toFixed(4)}
                  </Popup>
                </Marker>
              ))}
            </MarkerClusterGroup>
          </MapContainer>
        </div>
      )}
    </div>
  );
};

export default UserMapPage;
